import {
  SortableTh,
  SortingDirection,
  Table as CoreTable,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@components/core-ui/Table';
import { Switch } from '@components/core-ui/form-elements/Switch';
import Desktop from '@components/icons/Desktop.svg';
import Done from '@components/icons/Done.svg';
import DoubleDone from '@components/icons/DoubleDone.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { EntityIconRoundSm } from '@components/core-ui/EntityIcon/variations/EntityIconRoundSm';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { routes } from '@routes';
import { AdminUsersResponseUserDto } from 'app/API';
import { OrganizationLink } from './styles/OrganizationLink';
import { StyledLink } from './styles/StyledLink';
import { SwitchWrapper } from './styles/SwitchWrapper';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TProps } from './types/TProps';

export const Table = ({
  users,
  sorting,
  page,
  onPageChange,
  pageSize,
  setPageSize,
  currentUserId,
  onToggleEnabled,
  onVerifyEmail,
  onConfirmUser,
}: TProps) => {
  return (
    <>
      <TableWrapper>
        <CoreTable>
          <Thead $sticky>
            <Tr>
              <Th style={{ width: '1%' }}></Th>
              <SortableTh style={{ width: '20%' }} onClick={() => sorting.toggle('name')}>
                Name
                {sorting.field === 'name' && <SortingDirection reverse={sorting.reverse} />}
              </SortableTh>
              <Th style={{ width: '50%' }}>Contacts</Th>
              <SortableTh style={{ width: '1%' }} onClick={() => sorting.toggle('createdAt')}>
                Created
                {sorting.field === 'createdAt' && <SortingDirection reverse={sorting.reverse} />}
              </SortableTh>
              <Th style={{ width: '1%' }}>Organizations</Th>
              <Th style={{ width: '1%' }}>Enabled</Th>
              <Th style={{ width: '1%' }}>How heard about us</Th>
              <Th style={{ width: '1%' }}>Email verified</Th>
              <Th>Confirmed</Th>
              <Th style={{ width: '3%', paddingTop: '6px' }} title="Downloaded & Signed in with Desktop App">
                <Desktop width="16px" />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {users?.data?.map((data: AdminUsersResponseUserDto) => {
              const isSelf = currentUserId === data.id;

              return (
                <Tr key={data.id}>
                  <Td>
                    <EntityIconRoundSm
                      name={`${data.name} ${data.lastname}`}
                      src={data.avatar ?? undefined}
                      backgroundColor={!data.avatar ? 'var(--color-border)' : undefined}
                      color={!data.avatar ? 'var(--color-title)' : undefined}
                    />
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">
                      <StyledLink to={routes.adminUserDetails.make(data.id)}>
                        {data.name} {data.lastname}
                      </StyledLink>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">
                      <div>
                        <span style={{ display: 'block' }}>{data.email}</span>
                        <span style={{ display: 'block' }}>{data.phone}</span>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">{getAmericanTime(data.createdAt)}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">
                      <div>
                        {(data.organizations || []).map((item) => (
                          <OrganizationLink key={item.id} to={routes.adminOrganizationDetails.make(item.id)}>
                            {item.name}
                          </OrganizationLink>
                        ))}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">
                      <SwitchWrapper>
                        <Switch
                          checked={!data.disabled}
                          onChange={isSelf ? () => {} : () => onToggleEnabled(data)}
                          disabled={isSelf}
                        />
                      </SwitchWrapper>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center h-full">{data.howDidYouHearAboutUs}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center h-full">
                      {data.emailVerified ? (
                        <Done width="16px" />
                      ) : (
                        <OutlinedButton type="button" className="w-auto" onClick={() => onVerifyEmail(data)}>
                          Verify
                        </OutlinedButton>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center h-full">
                      {data.confirmed ? (
                        <Done width="16px" />
                      ) : (
                        <OutlinedButton type="button" className="w-auto" onClick={() => onConfirmUser(data)}>
                          Confirm
                        </OutlinedButton>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center h-full">
                      {data.signedInWithDesktop ? (
                        <DoubleDone width="24px" />
                      ) : data.downloadedDesktop ? (
                        <Done width="16px" />
                      ) : (
                        ''
                      )}
                    </div>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </CoreTable>
      </TableWrapper>
      <PagePagination
        totalItemsCount={users.count ?? 0}
        page={page}
        onPageChange={onPageChange}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};
